
import { AIModule } from '../../types';

export const GAMING_AI_MODULE_5: AIModule = {
  id: 'reinforcement-learning-games',
  title: 'Reinforcement Learning in Games',
  description: 'Apply reinforcement learning techniques to create AI agents that learn and adapt through gameplay experience.',
  duration: '4 hours',
  difficulty: 'hard',
  content: `
# Reinforcement Learning in Games

Reinforcement Learning (RL) enables AI agents to learn optimal strategies through trial and error, making them ideal for creating adaptive game AI that improves over time.

## RL Fundamentals for Games

### Core Concepts

**Agent**: The AI entity making decisions (NPC, bot, game AI)
**Environment**: The game world and its state
**State**: Current situation in the game
**Action**: Choices available to the agent
**Reward**: Feedback for actions taken
**Policy**: Strategy for choosing actions

### The RL Learning Process

1. **Observe** current game state
2. **Choose** an action based on policy
3. **Execute** action in game environment
4. **Receive** reward and new state
5. **Update** policy based on experience
6. **Repeat** to improve performance

## Q-Learning for Game AI

\`\`\`python
import numpy as np
import random
import matplotlib.pyplot as plt
from typing import Dict, List, Tuple, Optional
from enum import Enum
from dataclasses import dataclass
import json

class Action(Enum):
    MOVE_UP = 0
    MOVE_DOWN = 1
    MOVE_LEFT = 2
    MOVE_RIGHT = 3
    ATTACK = 4
    DEFEND = 5
    USE_ITEM = 6
    SPECIAL_ABILITY = 7

class GameState:
    """Represents a game state for RL training"""
    
    def __init__(self, player_pos: Tuple[int, int], enemy_pos: Tuple[int, int], 
                 player_health: int, enemy_health: int, items_available: int):
        self.player_pos = player_pos
        self.enemy_pos = enemy_pos
        self.player_health = player_health
        self.enemy_health = enemy_health
        self.items_available = items_available
        
    def to_tuple(self) -> Tuple:
        """Convert state to tuple for use as dictionary key"""
        return (self.player_pos, self.enemy_pos, self.player_health, 
                self.enemy_health, self.items_available)
    
    def distance_to_enemy(self) -> float:
        """Calculate distance between player and enemy"""
        return abs(self.player_pos[0] - self.enemy_pos[0]) + abs(self.player_pos[1] - self.enemy_pos[1])
    
    def is_terminal(self) -> bool:
        """Check if this is a terminal state"""
        return self.player_health <= 0 or self.enemy_health <= 0

class GameEnvironment:
    """Simple combat game environment for RL training"""
    
    def __init__(self, grid_size: int = 10):
        self.grid_size = grid_size
        self.reset()
        
    def reset(self) -> GameState:
        """Reset environment to initial state"""
        self.player_pos = (random.randint(0, self.grid_size-1), random.randint(0, self.grid_size-1))
        self.enemy_pos = (random.randint(0, self.grid_size-1), random.randint(0, self.grid_size-1))
        self.player_health = 100
        self.enemy_health = 100
        self.items_available = 3
        self.turn_count = 0
        
        return self.get_state()
    
    def get_state(self) -> GameState:
        """Get current game state"""
        return GameState(self.player_pos, self.enemy_pos, self.player_health, 
                        self.enemy_health, self.items_available)
    
    def step(self, action: Action) -> Tuple[GameState, float, bool]:
        """Execute action and return new state, reward, and done flag"""
        reward = 0
        self.turn_count += 1
        
        # Execute player action
        if action == Action.MOVE_UP:
            new_pos = (self.player_pos[0], max(0, self.player_pos[1] - 1))
            self.player_pos = new_pos
            reward -= 1  # Small penalty for movement
            
        elif action == Action.MOVE_DOWN:
            new_pos = (self.player_pos[0], min(self.grid_size-1, self.player_pos[1] + 1))
            self.player_pos = new_pos
            reward -= 1
            
        elif action == Action.MOVE_LEFT:
            new_pos = (max(0, self.player_pos[0] - 1), self.player_pos[1])
            self.player_pos = new_pos
            reward -= 1
            
        elif action == Action.MOVE_RIGHT:
            new_pos = (min(self.grid_size-1, self.player_pos[0] + 1), self.player_pos[1])
            self.player_pos = new_pos
            reward -= 1
            
        elif action == Action.ATTACK:
            distance = abs(self.player_pos[0] - self.enemy_pos[0]) + abs(self.player_pos[1] - self.enemy_pos[1])
            if distance <= 1:  # Adjacent attack
                damage = random.randint(15, 25)
                self.enemy_health -= damage
                reward += damage * 2  # Reward for successful attack
            else:
                reward -= 5  # Penalty for missed attack
                
        elif action == Action.DEFEND:
            reward += 5  # Small reward for defensive play
            
        elif action == Action.USE_ITEM:
            if self.items_available > 0:
                self.player_health = min(100, self.player_health + 20)
                self.items_available -= 1
                reward += 15  # Reward for healing
            else:
                reward -= 10  # Penalty for trying to use unavailable item
                
        elif action == Action.SPECIAL_ABILITY:
            if self.player_health > 20:  # Costs health to use
                self.player_health -= 20
                distance = abs(self.player_pos[0] - self.enemy_pos[0]) + abs(self.player_pos[1] - self.enemy_pos[1])
                if distance <= 2:  # Ranged special attack
                    damage = random.randint(25, 40)
                    self.enemy_health -= damage
                    reward += damage * 1.5
                else:
                    reward -= 15  # Penalty for missed special
        
        # Enemy AI (simple)
        self._enemy_turn()
        
        # Calculate additional rewards
        new_state = self.get_state()
        
        # Reward for being close to enemy (encourages engagement)
        distance = new_state.distance_to_enemy()
        if distance <= 2:
            reward += 5
        elif distance > 5:
            reward -= 2
        
        # Terminal state rewards
        done = new_state.is_terminal() or self.turn_count > 100
        if new_state.enemy_health <= 0:
            reward += 100  # Victory bonus
        elif new_state.player_health <= 0:
            reward -= 100  # Death penalty
        elif self.turn_count > 100:
            reward -= 50  # Timeout penalty
        
        return new_state, reward, done
    
    def _enemy_turn(self):
        """Simple enemy AI behavior"""
        # Move towards player
        if self.player_pos[0] > self.enemy_pos[0]:
            self.enemy_pos = (self.enemy_pos[0] + 1, self.enemy_pos[1])
        elif self.player_pos[0] < self.enemy_pos[0]:
            self.enemy_pos = (self.enemy_pos[0] - 1, self.enemy_pos[1])
        elif self.player_pos[1] > self.enemy_pos[1]:
            self.enemy_pos = (self.enemy_pos[0], self.enemy_pos[1] + 1)
        elif self.player_pos[1] < self.enemy_pos[1]:
            self.enemy_pos = (self.enemy_pos[0], self.enemy_pos[1] - 1)
        
        # Attack if adjacent
        distance = abs(self.player_pos[0] - self.enemy_pos[0]) + abs(self.player_pos[1] - self.enemy_pos[1])
        if distance <= 1:
            damage = random.randint(10, 20)
            self.player_health -= damage

class QLearningAgent:
    """Q-Learning agent for game AI"""
    
    def __init__(self, learning_rate: float = 0.1, discount_factor: float = 0.95, 
                 epsilon: float = 0.1, epsilon_decay: float = 0.995):
        self.learning_rate = learning_rate
        self.discount_factor = discount_factor
        self.epsilon = epsilon  # Exploration rate
        self.epsilon_decay = epsilon_decay
        self.min_epsilon = 0.01
        
        # Q-table: state -> action -> value
        self.q_table: Dict[Tuple, Dict[Action, float]] = {}
        
        # Training statistics
        self.episode_rewards = []
        self.episode_lengths = []
        self.win_rate_history = []
        
    def get_q_value(self, state: GameState, action: Action) -> float:
        """Get Q-value for state-action pair"""
        state_key = state.to_tuple()
        if state_key not in self.q_table:
            self.q_table[state_key] = {a: 0.0 for a in Action}
        return self.q_table[state_key][action]
    
    def set_q_value(self, state: GameState, action: Action, value: float):
        """Set Q-value for state-action pair"""
        state_key = state.to_tuple()
        if state_key not in self.q_table:
            self.q_table[state_key] = {a: 0.0 for a in Action}
        self.q_table[state_key][action] = value
    
    def choose_action(self, state: GameState, training: bool = True) -> Action:
        """Choose action using epsilon-greedy policy"""
        if training and random.random() < self.epsilon:
            # Exploration: random action
            return random.choice(list(Action))
        else:
            # Exploitation: best known action
            q_values = {action: self.get_q_value(state, action) for action in Action}
            return max(q_values, key=q_values.get)
    
    def update_q_value(self, state: GameState, action: Action, reward: float, 
                      next_state: GameState, done: bool):
        """Update Q-value using Q-learning formula"""
        current_q = self.get_q_value(state, action)
        
        if done:
            target_q = reward
        else:
            # Q(s,a) = Q(s,a) + α[r + γ max Q(s',a') - Q(s,a)]
            next_q_values = [self.get_q_value(next_state, a) for a in Action]
            target_q = reward + self.discount_factor * max(next_q_values)
        
        new_q = current_q + self.learning_rate * (target_q - current_q)
        self.set_q_value(state, action, new_q)
    
    def train(self, environment: GameEnvironment, episodes: int = 1000) -> Dict:
        """Train the agent using Q-learning"""
        print(f"Training Q-Learning agent for {episodes} episodes...")
        
        wins = 0
        for episode in range(episodes):
            state = environment.reset()
            total_reward = 0
            steps = 0
            
            while True:
                action = self.choose_action(state, training=True)
                next_state, reward, done = environment.step(action)
                
                self.update_q_value(state, action, reward, next_state, done)
                
                state = next_state
                total_reward += reward
                steps += 1
                
                if done:
                    break
            
            # Track statistics
            self.episode_rewards.append(total_reward)
            self.episode_lengths.append(steps)
            
            if environment.enemy_health <= 0:
                wins += 1
            
            # Decay epsilon
            if self.epsilon > self.min_epsilon:
                self.epsilon *= self.epsilon_decay
            
            # Calculate win rate every 100 episodes
            if (episode + 1) % 100 == 0:
                recent_wins = wins
                win_rate = recent_wins / 100
                self.win_rate_history.append(win_rate)
                wins = 0  # Reset for next 100 episodes
                
                avg_reward = np.mean(self.episode_rewards[-100:])
                print(f"Episode {episode + 1}: Win Rate = {win_rate:.2f}, "
                      f"Avg Reward = {avg_reward:.2f}, Epsilon = {self.epsilon:.3f}")
        
        return {
            'final_win_rate': self.win_rate_history[-1] if self.win_rate_history else 0,
            'total_states_learned': len(self.q_table),
            'avg_final_reward': np.mean(self.episode_rewards[-100:])
        }
    
    def evaluate(self, environment: GameEnvironment, episodes: int = 100) -> Dict:
        """Evaluate trained agent performance"""
        wins = 0
        total_rewards = []
        
        for _ in range(episodes):
            state = environment.reset()
            total_reward = 0
            
            while True:
                action = self.choose_action(state, training=False)
                state, reward, done = environment.step(action)
                total_reward += reward
                
                if done:
                    break
            
            total_rewards.append(total_reward)
            if environment.enemy_health <= 0:
                wins += 1
        
        return {
            'win_rate': wins / episodes,
            'average_reward': np.mean(total_rewards),
            'reward_std': np.std(total_rewards)
        }
    
    def save_model(self, filepath: str):
        """Save trained Q-table to file"""
        # Convert tuples to strings for JSON serialization
        serializable_q_table = {}
        for state_key, actions in self.q_table.items():
            state_str = str(state_key)
            serializable_q_table[state_str] = {action.name: value for action, value in actions.items()}
        
        model_data = {
            'q_table': serializable_q_table,
            'learning_rate': self.learning_rate,
            'discount_factor': self.discount_factor,
            'episode_rewards': self.episode_rewards,
            'win_rate_history': self.win_rate_history
        }
        
        with open(filepath, 'w') as f:
            json.dump(model_data, f, indent=2)
    
    def plot_training_progress(self):
        """Plot training progress charts"""
        fig, ((ax1, ax2), (ax3, ax4)) = plt.subplots(2, 2, figsize=(15, 10))
        
        # Episode rewards
        ax1.plot(self.episode_rewards)
        ax1.set_title('Episode Rewards Over Time')
        ax1.set_xlabel('Episode')
        ax1.set_ylabel('Total Reward')
        ax1.grid(True)
        
        # Moving average of rewards
        window = 100
        if len(self.episode_rewards) >= window:
            moving_avg = np.convolve(self.episode_rewards, np.ones(window)/window, mode='valid')
            ax2.plot(moving_avg)
            ax2.set_title(f'Moving Average Reward (window={window})')
            ax2.set_xlabel('Episode')
            ax2.set_ylabel('Average Reward')
            ax2.grid(True)
        
        # Win rate history
        if self.win_rate_history:
            ax3.plot(self.win_rate_history)
            ax3.set_title('Win Rate Over Training')
            ax3.set_xlabel('Training Batch (x100 episodes)')
            ax3.set_ylabel('Win Rate')
            ax3.grid(True)
        
        # Episode lengths
        ax4.plot(self.episode_lengths)
        ax4.set_title('Episode Lengths')
        ax4.set_xlabel('Episode')
        ax4.set_ylabel('Steps')
        ax4.grid(True)
        
        plt.tight_layout()
        plt.show()

class PolicyGradientAgent:
    """Policy Gradient agent for more complex game scenarios"""
    
    def __init__(self, state_size: int, action_size: int, learning_rate: float = 0.01):
        self.state_size = state_size
        self.action_size = action_size
        self.learning_rate = learning_rate
        
        # Simple neural network weights (for demonstration)
        self.weights = np.random.randn(state_size, action_size) * 0.1
        self.bias = np.zeros(action_size)
        
        # Training history
        self.episode_rewards = []
        self.policy_losses = []
    
    def state_to_vector(self, state: GameState) -> np.ndarray:
        """Convert game state to feature vector"""
        # Normalize positions
        player_x, player_y = state.player_pos
        enemy_x, enemy_y = state.enemy_pos
        
        features = np.array([
            player_x / 10.0,  # Normalized player position
            player_y / 10.0,
            enemy_x / 10.0,   # Normalized enemy position
            enemy_y / 10.0,
            state.player_health / 100.0,  # Normalized health
            state.enemy_health / 100.0,
            state.items_available / 3.0,  # Normalized items
            state.distance_to_enemy() / 20.0  # Normalized distance
        ])
        
        return features
    
    def forward(self, state_vector: np.ndarray) -> np.ndarray:
        """Forward pass through network"""
        logits = np.dot(state_vector, self.weights) + self.bias
        # Softmax activation
        exp_logits = np.exp(logits - np.max(logits))
        return exp_logits / np.sum(exp_logits)
    
    def choose_action(self, state: GameState) -> Tuple[Action, float]:
        """Choose action using policy network"""
        state_vector = self.state_to_vector(state)
        action_probs = self.forward(state_vector)
        
        # Sample action from probability distribution
        action_idx = np.random.choice(len(action_probs), p=action_probs)
        action = list(Action)[action_idx]
        
        return action, action_probs[action_idx]
    
    def train_episode(self, environment: GameEnvironment) -> float:
        """Train on a single episode using REINFORCE algorithm"""
        states = []
        actions = []
        rewards = []
        action_probs = []
        
        state = environment.reset()
        total_reward = 0
        
        # Collect episode data
        while True:
            action, prob = self.choose_action(state)
            next_state, reward, done = environment.step(action)
            
            states.append(self.state_to_vector(state))
            actions.append(list(Action).index(action))
            rewards.append(reward)
            action_probs.append(prob)
            
            state = next_state
            total_reward += reward
            
            if done:
                break
        
        # Calculate discounted returns
        returns = []
        R = 0
        for r in reversed(rewards):
            R = r + 0.99 * R  # Discount factor of 0.99
            returns.insert(0, R)
        
        returns = np.array(returns)
        returns = (returns - np.mean(returns)) / (np.std(returns) + 1e-8)  # Normalize
        
        # Update policy
        for i in range(len(states)):
            state_vector = states[i]
            action_idx = actions[i]
            return_value = returns[i]
            
            # Calculate gradients
            action_probs = self.forward(state_vector)
            
            # Policy gradient update
            grad_log_prob = np.zeros(self.action_size)
            grad_log_prob[action_idx] = 1.0 / action_probs[action_idx]
            
            # Update weights
            self.weights += self.learning_rate * return_value * np.outer(state_vector, grad_log_prob)
            self.bias += self.learning_rate * return_value * grad_log_prob
        
        self.episode_rewards.append(total_reward)
        return total_reward

# Example usage and comparison
def demonstrate_rl_in_games():
    """Demonstrate different RL approaches for game AI"""
    
    print("=== Reinforcement Learning in Games Demo ===")
    
    # Create environment
    env = GameEnvironment(grid_size=8)
    
    print("\\n--- Training Q-Learning Agent ---")
    q_agent = QLearningAgent(learning_rate=0.1, epsilon=0.3)
    q_results = q_agent.train(env, episodes=1000)
    
    print(f"Q-Learning Results:")
    print(f"  Final Win Rate: {q_results['final_win_rate']:.2f}")
    print(f"  States Learned: {q_results['total_states_learned']}")
    print(f"  Average Final Reward: {q_results['avg_final_reward']:.2f}")
    
    # Evaluate Q-Learning agent
    q_eval = q_agent.evaluate(env, episodes=100)
    print(f"\\nQ-Learning Evaluation:")
    print(f"  Win Rate: {q_eval['win_rate']:.2f}")
    print(f"  Average Reward: {q_eval['average_reward']:.2f}")
    
    print("\\n--- Training Policy Gradient Agent ---")
    pg_agent = PolicyGradientAgent(state_size=8, action_size=len(Action))
    
    # Train policy gradient agent
    pg_rewards = []
    for episode in range(500):
        reward = pg_agent.train_episode(env)
        pg_rewards.append(reward)
        
        if (episode + 1) % 100 == 0:
            avg_reward = np.mean(pg_rewards[-100:])
            print(f"Episode {episode + 1}: Average Reward = {avg_reward:.2f}")
    
    # Compare final performance
    print("\\n=== Performance Comparison ===")
    print(f"Q-Learning final win rate: {q_eval['win_rate']:.2f}")
    
    # Quick evaluation of policy gradient
    pg_wins = 0
    for _ in range(100):
        state = env.reset()
        while True:
            action, _ = pg_agent.choose_action(state)
            state, _, done = env.step(action)
            if done:
                break
        if env.enemy_health <= 0:
            pg_wins += 1
    
    print(f"Policy Gradient win rate: {pg_wins / 100:.2f}")

# Run demonstration
demonstrate_rl_in_games()
\`\`\`

## Deep Reinforcement Learning

### Deep Q-Networks (DQN)
- Neural networks for Q-value approximation
- Experience replay for stable learning
- Target networks for improved convergence

### Actor-Critic Methods
- Separate policy and value function networks
- Continuous action spaces
- More stable learning than policy gradients alone

### Advanced Techniques
- **Prioritized Experience Replay**: Focus on important experiences
- **Double DQN**: Reduce overestimation bias
- **Dueling DQN**: Separate state value and advantage functions

## RL Applications in Games

### Real-Time Strategy (RTS)
- Resource management optimization
- Unit micro-management
- Strategic decision making

### Fighting Games
- Combo execution and timing
- Opponent adaptation
- Frame-perfect reactions

### Racing Games
- Optimal racing lines
- Overtaking strategies
- Dynamic difficulty adjustment

### Procedural Content
- Learning player preferences
- Adaptive level generation
- Personalized content creation

Reinforcement Learning enables game AI that continuously improves and adapts, creating more challenging and engaging opponents that learn from player behavior.
  `,
  quiz: {
    id: 'rl-games-quiz',
    title: 'Reinforcement Learning in Games Quiz',
    passThreshold: 75,
    questions: [
      {
        id: 'q1',
        question: 'What is the main advantage of Q-learning over rule-based AI?',
        type: 'multiple-choice',
        options: [
          'It requires less computational power',
          'It learns optimal strategies through experience',
          'It is easier to program',
          'It works faster in real-time'
        ],
        correctAnswer: 1,
        explanation: 'Q-learning discovers optimal strategies through trial and error, adapting to different situations without manually programmed rules.',
        difficulty: 'hard',
        points: 20
      },
      {
        id: 'q2',
        question: 'What does the epsilon parameter control in epsilon-greedy exploration?',
        type: 'multiple-choice',
        options: [
          'Learning rate',
          'Discount factor',
          'Balance between exploration and exploitation',
          'Reward scaling'
        ],
        correctAnswer: 2,
        explanation: 'Epsilon controls the probability of choosing random actions (exploration) versus choosing the best known action (exploitation).',
        difficulty: 'hard',
        points: 15
      }
    ]
  }
};
